import { useFindMany, useAction } from "@gadgetinc/react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  PlusIcon,
  RectangleStackIcon,
} from "@heroicons/react/16/solid";
import { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { api } from "../../../api";
import { OverflowFrame } from "../../../externalComponents";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { PageHeading } from "../../../externalComponents/PageHeading";
import { SettingsSidebar } from "../../../externalComponents/SettingsSidebar";
import { Badge } from "../../../externalComponents/tailwind/badge";
import { Button } from "../../../externalComponents/tailwind/button";
import EmptyState from "../../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../externalComponents/tailwind/table";
import { formatDateTime } from "../../../functions/date-utils";
import { CustomerContext } from "../../../providers";
import { encodeFiltersToUrl } from "../../contacts/utils/filterUrlUtils";

export default function ImportsPage() {
  const { customer } = useContext(CustomerContext);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [{ data, fetching }] = useFindMany(api.csvImport, {
    live: true,
    filter: {
      status: { isSet: true },
      tag: { isSet: true },
    },
    select: {
      id: true,
      status: true,
      createdAt: true,
      processedAt: true,
      rowCount: true,
      successCount: true,
      duplicateCount: true,
      errorCount: true,
      errorLog: true,
      tag: { name: true, id: true },
    },
    sort: {
      createdAt: "Descending",
    },
  });

  const [, checkImportProgress] = useAction(api.settings.csvImportUpdateProgression);
  const dataRef = useRef();
  dataRef.current = data;

  useEffect(() => {
    const checkAllProcessingImports = async () => {
      const currentData = dataRef.current;
      const processingImports = currentData?.filter(imp => imp.status === "processing") || [];
      
      if (processingImports.length === 0) {
        return;
      }

      for (const imp of processingImports) {
        try {
          await checkImportProgress({ csvImportId: imp.id });
        } catch (error) {
          console.error(`Error checking import ${imp.id}:`, error);
        }
      }
    };

    const interval = setInterval(() => {
      checkAllProcessingImports();
    }, 30000);

    return () => clearInterval(interval);
  }, [checkImportProgress]);

  const CsvImportList = () => {
    const totalItems = data?.length || 0;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = totalItems ? data.slice(startIndex, endIndex) : [];

    return (
      <div className="mx-3 md:mx-0">
        {fetching ? (
          <LoadingSpinner />
        ) : data && data.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Creation date</TableHeader>
                <TableHeader>Auto Tag name</TableHeader>
                <TableHeader className="text-center">Rows</TableHeader>
                <TableHeader className="text-center">Duplicates</TableHeader>
                <TableHeader className="text-center">Imported</TableHeader>
                <TableHeader className="text-center">Status</TableHeader>
                <TableHeader className="text-center">Errors</TableHeader>
                <TableHeader className="text-center">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="w-[10%]">
                    {formatDateTime(row.createdAt, "shortTimeMediumDate")}
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Badge>{row.tag?.name || "Tag deleted"}</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {row.rowCount || 0}
                  </TableCell>
                  <TableCell className="text-center">
                    {row.duplicateCount || 0}
                  </TableCell>
                  <TableCell className="text-center w-[20%]">
                    {row.successCount || 0} (
                    {(
                      ((row.successCount || 0) /
                        (row.rowCount - (row.duplicateCount || 0))) *
                      100
                    ).toFixed(0)}
                    %)
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge
                      color={
                        row.status === "success"
                          ? "lime"
                          : row.status === "processing"
                            ? "cyan"
                            : row.status === "pending"
                              ? "zinc"
                              : "rose"
                      }
                    >
                      {row.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center ">
                    {row.errorLog && (
                      <div className="flex justify-center">
                        <span
                          className="text-red-500 cursor-help"
                          title={row.errorLog}
                        >
                          ⚠️
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center items-center gap-2">
                      {row.status === "success" && row.tag?.name && (
                        <div className="relative group">
                          <button
                            onClick={() => {
                              // Create a filter for tags containing the auto tag name
                              const filterGroups = [
                                {
                                  ors: [
                                    {
                                      type: "tag",
                                      operator: "contains",
                                      operatorLabel: "contains",
                                      requiresValue: true,
                                      value: row.tag.name,
                                      tagId: row.tag.id,
                                    },
                                  ],
                                },
                              ];
                              const encodedFilters =
                                encodeFiltersToUrl(filterGroups);
                              navigate(`/contacts?filters=${encodedFilters}`);
                            }}
                            className="p-1 text-gray-600 hover:text-pink transition-colors"
                            aria-label="Create segment"
                          >
                            <RectangleStackIcon className="h-4 w-4" />
                          </button>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-white text-gray-900 text-sm rounded-lg shadow-lg border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Create segment
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {data && data.length > 0 && (
          <div className="flex justify-start my-4">
            <Button
              color="secondaryButton"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ArrowLeftIcon />
              Previous
            </Button>
            <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
              {currentPage}
            </Badge>
            <Button
              color="secondaryButton"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
              <ArrowRightIcon />
            </Button>
          </div>
        )}
        {!fetching && (!data || data.length === 0) && (
          <EmptyState
            heading="You have no CSV imports"
            action={async () => {
              if (customer.isSubscribed) {
                navigate("/contacts/import");
              }
            }}
            icon={<PlusIcon />}
            actionText="Import contacts"
            img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          />
        )}
      </div>
    );
  };

  return (
    <>
      <SettingsSidebar>
        <PageHeading
          title="Imports"
          primaryAction="New import"
          handlePrimaryAction={() => navigate("/contacts/import")}
        />
        <OverflowFrame>
          <CsvImportList />
        </OverflowFrame>
      </SettingsSidebar>
    </>
  );
}
